import React from 'react';
import { Rating as MuiRating, Typography, Box } from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { usePlaceRating } from '../hooks/usePlaceRating';
import theme from './Theme';

interface RatingComponentProps {
  placeId: string;
  averageRating?: number;
  rateVolume?: number;
  isAuthenticated: boolean;
  authToken: string | null;
  refetchPlaceData?: () => void;
}

/**
 * Rating component displaying average rating and allowing user to rate place.
 * Uses TanStack Query via usePlaceRating hook.
 */
export const RatingComponent: React.FC<RatingComponentProps> = ({
  placeId,
  averageRating = 0,
  rateVolume = 0,
  isAuthenticated,
  authToken,
  refetchPlaceData,
}) => {
  const { userId } = useAuth();
  const { userRating, canRate, isLoading, rate, isSubmitting } = usePlaceRating(
    userId,
    placeId
  );

  const displayRating = userRating || averageRating;
  const hasRatings = rateVolume > 0;

  const handleRatingChange = (_event: React.SyntheticEvent, newValue: number | null) => {
    if (!isAuthenticated || !canRate || !newValue) return;
    
    rate(newValue);
    
    // Optionally refetch place data after rating
    if (refetchPlaceData) {
      setTimeout(() => refetchPlaceData(), 500); // Small delay for mutation to complete
    }
  };

  return (
    <Box>
      <Box display="flex" alignItems="center" gap={1}>
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          Rating
        </Typography>
        {hasRatings ? (
          <>
            <MuiRating
              value={displayRating}
              readOnly={!isAuthenticated || !canRate || isSubmitting}
              onChange={handleRatingChange}
              precision={0.5}
              size="large"
              sx={{
                '& .MuiRating-iconFilled': {
                  color: theme.palette.accent.main,
                },
                '& .MuiRating-iconEmpty': {
                  color: theme.palette.accent.main,
                },
              }}
            />
            <Typography variant="body2" color="text.secondary">
              {averageRating.toFixed(1)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              ({rateVolume} {rateVolume === 1 ? 'rating' : 'ratings'})
            </Typography>
          </>
        ) : (
          <Typography variant="body2" color="text.secondary">
            Not rated yet
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default RatingComponent;
